import React, { useState, useEffect } from 'react';
import { 
  Target, Calendar, TrendingUp, Award, 
  Building2, Users, ArrowUpRight, Sparkles, UserCheck, Crown,
  Wallet, History, Clock, ShieldCheck, DollarSign, RefreshCw, CheckCircle2, AlertCircle
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerPerformance = () => {
  const currentMonthStr = new Date().toISOString().substring(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
  const [data, setData] = useState(null);
  const [commData, setCommData] = useState({
    wallet: {
      diamond_balance: 0,
      monthly_earned_diamonds: 0,
      pending_diamonds: 0
    },
    pending_commissions: [],
    ledger: []
  });
  const [loading, setLoading] = useState(true);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [perfRes, commRes] = await Promise.allSettled([
        managerAPI.getPerformance({ month: selectedMonth }),
        managerAPI.getCommissions({ month: selectedMonth })
      ]);
      if (perfRes.status === 'fulfilled' && perfRes.value?.data?.success) {
        setData(perfRes.value.data.performance);
      }
      if (commRes.status === 'fulfilled' && commRes.value?.data?.success) {
        setCommData({
          wallet: commRes.value.data.wallet || { diamond_balance: 0, monthly_earned_diamonds: 0, pending_diamonds: 0 },
          pending_commissions: commRes.value.data.pending_commissions || [],
          ledger: commRes.value.data.ledger || []
        });
      }
    } catch (err) {
      console.error("Failed to load performance & commissions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [selectedMonth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const targetAmount = data?.target_amount || 5000000;
  const achievedAmount = data?.achieved_amount || 0;
  const percentage = data?.percentage || 0;
  const breakdown = data?.breakdown || [];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <Target className="text-amber-400" size={28} />
              <span>Manager Performance & Commission Ledger</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Executive monthly targets, managerial commission wallet, and automated hierarchy ledger.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-[#0b0f19]/80 border border-indigo-500/20 rounded-2xl px-4 py-2.5">
              <Calendar size={18} className="text-indigo-400" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent text-white font-bold text-sm outline-none cursor-pointer"
              />
            </div>
            <button
              onClick={() => fetchAllData()}
              title="Refresh Stats"
              className="p-2.5 rounded-2xl bg-[#0b0f19]/80 hover:bg-white/10 border border-indigo-500/20 text-indigo-400 hover:text-white transition-colors cursor-pointer"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Manager Commission Wallet Highlight Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: Available Wallet */}
          <div className="bg-gradient-to-br from-emerald-950/40 via-[#0b0f19] to-[#0b0f19] border border-emerald-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Manager Wallet</span>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
                <Wallet size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-black text-white font-mono">
              {(commData.wallet?.diamond_balance || 0).toLocaleString()} <span className="text-emerald-400 text-lg">💎</span>
            </h3>
            <p className="text-xs text-slate-400 mt-2">Available Diamond Balance in Manager Wallet</p>
          </div>

          {/* Card 2: Pending Diamonds */}
          <div className="bg-gradient-to-br from-amber-950/40 via-[#0b0f19] to-[#0b0f19] border border-amber-500/25 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Pending Diamonds</span>
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center">
                <Sparkles size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-black text-amber-400 font-mono">
              {(commData.wallet?.pending_diamonds || 0).toLocaleString()} <span className="text-lg">💎</span>
            </h3>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
              {commData.wallet?.is_eligible_for_release ? (
                <span className="text-emerald-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 size={12} /> Released (10 Days Met)
                </span>
              ) : (
                <span className="text-amber-300 font-semibold flex items-center gap-1">
                  <AlertCircle size={12} /> {commData.wallet?.days_remaining ?? Math.max(0, 10 - (commData.wallet?.valid_days || 0))} days to unlock
                </span>
              )}
            </p>
          </div>

          {/* Card 3: Manager Valid Days */}
          <div className="bg-gradient-to-br from-indigo-950/40 via-[#0b0f19] to-[#0b0f19] border border-indigo-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Active Valid Days</span>
              <div className="w-10 h-10 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center">
                <Calendar size={20} />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-black text-white font-mono">
                {commData.wallet?.valid_days || 0}
              </h3>
              <span className="text-sm text-slate-400 font-bold">/ 10 Days</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-1.5 mt-2 overflow-hidden border border-white/10">
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(100, Math.round(((commData.wallet?.valid_days || 0) / 10) * 100))}%` }}
              ></div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Daily mic/seat timer active</p>
          </div>

          {/* Card 4: Earned This Month */}
          <div className="bg-gradient-to-br from-purple-950/40 via-[#0b0f19] to-[#0b0f19] border border-purple-500/20 rounded-[28px] p-6 backdrop-blur-xl relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Earned This Month</span>
              <div className="w-10 h-10 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center">
                <ShieldCheck size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-black text-white font-mono">
              {(commData.wallet?.monthly_earned_diamonds || 0).toLocaleString()} <span className="text-purple-400 text-lg">💎</span>
            </h3>
            <p className="text-xs text-slate-400 mt-2">Managerial Commission ({selectedMonth})</p>
          </div>
        </div>

        {/* Big Progress Card */}
        <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-r from-indigo-950/70 via-purple-950/50 to-slate-900 border border-indigo-500/20 p-8 md:p-10 shadow-2xl backdrop-blur-xl">
          <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold uppercase">
                <Crown size={14} className="text-indigo-400" />
                <span>Executive Monthly Target</span>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-black text-white">
                  {achievedAmount.toLocaleString()} <span className="text-lg text-slate-400 font-normal">/ {targetAmount.toLocaleString()} Coins</span>
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  Cumulative coins generated by all active hosts across your entire BD network this month.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-bold">
                  <span className="text-indigo-300">Goal Progress</span>
                  <span className="text-amber-400">{percentage}% Achieved</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-3 overflow-hidden p-0.5 border border-white/10">
                  <div 
                    className="bg-gradient-to-r from-indigo-500 via-purple-500 to-amber-400 h-full rounded-full transition-all duration-1000 shadow-lg shadow-indigo-500/30"
                    style={{ width: `${Math.min(100, percentage)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Right Stat Widget */}
            <div className="bg-[#0c0a1f]/80 border border-indigo-500/20 rounded-3xl p-6 text-center backdrop-blur-xl">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mx-auto mb-3">
                <Award size={32} />
              </div>
              <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Manager Status</h3>
              <p className="text-xl font-black text-white mt-1">
                {percentage >= 100 ? 'Target Achieved 🎉' : `${(targetAmount - achievedAmount > 0 ? (targetAmount - achievedAmount).toLocaleString() : 0)} Coins Remaining`}
              </p>
              <p className="text-xs text-slate-500 mt-2">Active Month: {selectedMonth}</p>
            </div>
          </div>
        </div>

        {/* Manager Real-Time Commission Ledger */}
        <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <History size={20} className="text-emerald-400" />
                <span>Real-Time Commission Ledger</span>
              </h3>
              <p className="text-xs text-slate-400">Automated ledger transactions for managerial overrides</p>
            </div>
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-xl border border-emerald-500/20">
              {commData.ledger?.length || 0} Transactions
            </span>
          </div>

          {commData.ledger?.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              <Clock size={40} className="mx-auto mb-3 opacity-30" />
              No manager commission transactions recorded yet for this month.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.01]">
                    <th className="py-4 px-6">Date & Time</th>
                    <th className="py-4 px-4">Transaction Type</th>
                    <th className="py-4 px-4">Source Host / Partner</th>
                    <th className="py-4 px-4">Description</th>
                    <th className="py-4 px-6 text-right">Commission (💎)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm font-medium">
                  {commData.ledger.map((tx) => (
                    <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 px-6 text-xs text-slate-400">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20">
                          {tx.transaction_type}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-bold text-white text-xs">{tx.source_user_name || 'System Reward'}</p>
                        {tx.source_xoxo_id && (
                          <p className="text-[10px] text-slate-400 font-mono">ID: {tx.source_xoxo_id}</p>
                        )}
                      </td>
                      <td className="py-4 px-4 text-xs text-slate-300">
                        {tx.reason || tx.description || 'Managerial commission override'}
                      </td>
                      <td className="py-4 px-6 text-right font-mono font-bold text-emerald-400">
                        +{(Number(tx.amount) || 0).toLocaleString()} 💎
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* BD Breakdown Table */}
        <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <UserCheck size={20} className="text-indigo-400" />
                <span>BD Contribution Breakdown</span>
              </h3>
              <p className="text-xs text-slate-400">Coins generated per Business Development partner</p>
            </div>
          </div>

          {breakdown.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No BD performance data recorded for this month.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.01]">
                    <th className="py-4 px-6">BD Partner</th>
                    <th className="py-4 px-4 text-center">Agencies</th>
                    <th className="py-4 px-4 text-center">Hosts</th>
                    <th className="py-4 px-6 text-right">Coins Generated</th>
                    <th className="py-4 px-6 text-right">Share of Network</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm font-medium">
                  {breakdown.map((bd) => {
                    const share = achievedAmount > 0 ? Math.round((bd.achieved_coins / achievedAmount) * 100) : 0;
                    return (
                      <tr key={bd.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm">
                              {bd.bd_name?.charAt(0) || 'B'}
                            </div>
                            <div>
                              <p className="font-bold text-white text-sm">{bd.bd_name}</p>
                              <p className="text-xs text-indigo-400 font-mono mt-0.5">ID: {bd.user_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-slate-300 font-bold text-xs">
                            {bd.agencies_count || 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-cyan-500/10 text-cyan-400 font-bold text-xs">
                            {bd.hosts_count || 0}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="font-bold text-amber-400 font-mono text-base">
                            {(bd.achieved_coins || 0).toLocaleString()}
                          </span>
                          <span className="text-xs text-slate-400 ml-1">coins</span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-500/15 text-indigo-300 font-bold text-xs border border-indigo-500/20 font-mono">
                            {share}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
  );
};

export default ManagerPerformance;
